import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import usePageBackground from "../hooks/usePageBackground";
import { useStores } from "../hooks/useStores";
import { getRetailerConfig, prepareUserUpdateData, type RetailerType } from "../config/retailers";
import { getDollarGeneralCategories, getTargetCategories } from "../services/CategoriesService";
import { geocodeZipCode } from "../utils/geocoding";
import { getRetailerStoreLimit } from "../utils/retailerStoreLimits";
import { parseStoreChangeError } from "../utils/userUtils";
import { track } from "../utils/analytics";
import { extraRetailersToSetUp, savesOwnCategories } from "../utils/moreStores";
import { calculateDistance, normalizeStore, type StoreType } from "../components/BotFiltersOnboardingModal.utils";
import {
  BackButton,
  ErrorBox,
  ONB_BG,
  ONB_BORDER,
  ONB_CTA,
  ONB_MUTED,
  ONB_SEL_TINT,
  ONB_TEXT2,
  OnboardingScreen,
  PrimaryCta,
  StoreChecklist,
  TopBar,
  type OnboardingStore,
} from "../components/onboarding/OnboardingParts";

const NEARBY_LIMIT = 40;

/** Retailer display names for this screen (the config shortens Home Depot to "HD"). */
function retailerName(retailer: RetailerType): string {
  return getRetailerConfig(retailer).displayName;
}

/** Categories to save with a retailer's stores, or undefined to leave the field out entirely. */
async function categoriesFor(retailer: RetailerType): Promise<string[] | undefined> {
  if (!getRetailerConfig(retailer).features.categories || !savesOwnCategories(retailer)) return undefined;
  try {
    const list =
      retailer === "target" ? await getTargetCategories() : retailer === "dollar_general" ? await getDollarGeneralCategories() : [];
    const keys = (list || []).map((category) => category.rootKey).filter(Boolean);
    // Never send an empty list: the key alone would clear the retailer's categories.
    return keys.length > 0 ? keys : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Web port of pinto/app/more-stores.tsx. After checkout ("You're in"), Plus/Pro
 * accounts pick the other in-store retailers they shop at, then their stores
 * for each one, then land on the deals.
 */
export default function MoreStoresPage() {
  useEnforceLightMode();
  usePageBackground(ONB_BG);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, load } = useUserContext();
  // Captured once so the list does not reshuffle as stores get saved.
  const [available] = useState(() => extraRetailersToSetUp(user));
  const [picked, setPicked] = useState<RetailerType[]>([]);
  const [queue, setQueue] = useState<RetailerType[] | null>(null);
  const [index, setIndex] = useState(0);

  const goToDeals = () => {
    void load();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (available.length === 0) {
      goToDeals();
      return;
    }
    track("onboarding_more_stores_shown", { count: available.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (available.length === 0) return null;

  const toggle = (retailer: RetailerType) =>
    setPicked((current) => (current.includes(retailer) ? current.filter((r) => r !== retailer) : [...current, retailer]));

  const start = () => {
    if (picked.length === 0) return;
    // Keep the screen's order, not tap order.
    const ordered = available.filter((r) => picked.includes(r));
    track("onboarding_more_stores_picked", { count: ordered.length, retailers: ordered.join(",") });
    setQueue(ordered);
    setIndex(0);
  };

  if (queue) {
    const retailer = queue[index];
    return (
      <RetailerStoresStep
        key={retailer}
        retailer={retailer}
        isLast={index === queue.length - 1}
        onBack={() => (index > 0 ? setIndex(index - 1) : setQueue(null))}
        onDone={() => (index + 1 < queue.length ? setIndex(index + 1) : goToDeals())}
      />
    );
  }

  const n = picked.length;
  const allPicked = n === available.length;
  return (
    <OnboardingScreen testId="more-stores">
      <div className="flex-1 px-6 pb-6 pt-10">
        <h1 className="text-[31px] font-black leading-[35px] tracking-[-0.7px]">{t("onboarding2.more_title")}</h1>
        <p className="mt-2 text-base font-bold leading-[22px]" style={{ color: ONB_TEXT2 }}>{t("onboarding2.more_sub")}</p>

        <div className="mb-2.5 mt-[22px] flex items-center justify-between">
          <span className="text-[13px] font-extrabold tracking-[0.4px]" style={{ color: ONB_MUTED }}>{t("onboarding2.more_included")}</span>
          <button
            type="button"
            onClick={() => setPicked(allPicked ? [] : available)}
            className="text-[15px] font-black hover:underline"
            style={{ color: "#6D28D9" }}
          >
            {allPicked ? t("onboarding2.more_clear") : t("onboarding2.more_select_all")}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {available.map((retailer) => {
            const selected = picked.includes(retailer);
            return (
              <button
                key={retailer}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() => toggle(retailer)}
                className="flex min-h-[56px] w-full items-center gap-3 rounded-2xl px-[18px] text-left transition"
                style={{
                  border: selected ? `2px solid ${ONB_CTA}` : `1px solid ${ONB_BORDER}`,
                  backgroundColor: selected ? ONB_SEL_TINT : "#FFFFFF",
                }}
              >
                <span className="flex-1 truncate text-[17px] font-black leading-[22px]">{retailerName(retailer)}</span>
                <span
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-full border-2"
                  style={selected ? { backgroundColor: ONB_CTA, borderColor: ONB_CTA } : { borderColor: "#C9C3D6" }}
                >
                  {selected ? <Check className="h-[15px] w-[15px] text-white" strokeWidth={3} /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-0.5 px-6 pb-[max(env(safe-area-inset-bottom),12px)] pt-2.5">
        <PrimaryCta onClick={start} disabled={n === 0}>
          {n === 0 ? t("onboarding2.more_pick_one") : t("onboarding2.more_find_stores", { count: n })}
        </PrimaryCta>
        <button
          type="button"
          onClick={() => { track("onboarding_more_stores_skipped"); goToDeals(); }}
          className="h-11 text-[15px] font-extrabold hover:underline"
          style={{ color: ONB_TEXT2 }}
        >
          {t("onboarding2.more_skip")}
        </button>
      </div>
    </OnboardingScreen>
  );
}

/** "Which Target stores do you shop at?" — the same checklist as the paywall's store steps. */
function RetailerStoresStep({
  retailer,
  isLast,
  onBack,
  onDone,
}: {
  retailer: RetailerType;
  isLast: boolean;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { user, updateUser } = useUserContext();
  const name = retailerName(retailer);
  const limit = Math.max(1, getRetailerStoreLimit(user?.retailers ?? [], retailer));
  const { stores, loading, error } = useStores(retailer);
  const [nearby, setNearby] = useState<OnboardingStore[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const zip = user?.zipCode ?? "";

  const normalized = useMemo(
    () =>
      ((stores as unknown as StoreType[] | null) || [])
        .map((store) => normalizeStore(store, retailer))
        .filter((store) => store.normalizedId),
    [stores, retailer]
  );

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    void (async () => {
      const coords = /^\d{5}$/.test(zip) ? await geocodeZipCode(zip).catch(() => null) : null;
      if (cancelled) return;
      setNearby(
        coords
          ? normalized
              .filter((store) => store.normalizedGeo?.lat && store.normalizedGeo?.long)
              .map((store) => ({
                ...store,
                distance: calculateDistance(coords.lat, coords.long, store.normalizedGeo!.lat, store.normalizedGeo!.long),
              }))
              .filter((store) => store.distance <= 100)
              .sort((a, b) => a.distance - b.distance)
              .slice(0, NEARBY_LIMIT)
          : normalized.slice(0, NEARBY_LIMIT)
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, normalized, zip]);

  const save = async () => {
    if (selected.length === 0 || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const categories = await categoriesFor(retailer);
      await updateUser(prepareUserUpdateData(retailer, categories ? { stores: selected, categories } : { stores: selected }));
      track("onboarding_more_stores_saved", { retailer, selected_count: selected.length });
      onDone();
    } catch (err) {
      setSaveError(parseStoreChangeError(err) || t("onboarding2.more_save_failed"));
      setSaving(false);
    }
  };

  const n = selected.length;
  const ctaLabel =
    n === 0
      ? t("onboarding2.stores_pick_one")
      : isLast
        ? t("onboarding2.more_done")
        : t(n === 1 ? "onboarding2.stores_continue_one" : "onboarding2.stores_continue_n", { count: n });

  return (
    <OnboardingScreen testId={`more-stores-${retailer}`} className="h-[100svh]">
      <TopBar left={<BackButton onClick={onBack} label={t("onboarding2.back")} />} />
      <div className="space-y-1.5 px-6 pb-3 pt-2">
        <h1 className="text-[30px] font-black leading-[34px] tracking-[-0.7px]">{t("onboarding2.more_which_stores", { retailer: name })}</h1>
        <p className="text-[15px] font-semibold leading-[21px]" style={{ color: ONB_TEXT2 }}>
          {t("onboarding2.stores_hint", { count: limit })}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-4">
        <ErrorBox error={saveError} />
        <StoreChecklist
          retailer={retailer}
          stores={nearby}
          selected={selected}
          limit={limit}
          onChange={setSelected}
          zip={zip}
          emptyText={error ? t("onboarding2.stores_load_error") : t("onboarding2.more_none_nearby", { retailer: name })}
        />
      </div>
      <div className="flex flex-col gap-0.5 px-6 pb-[max(env(safe-area-inset-bottom),12px)] pt-3">
        <PrimaryCta onClick={() => void save()} disabled={n === 0} busy={saving}>
          {ctaLabel}
        </PrimaryCta>
        <button
          type="button"
          onClick={() => { track("onboarding_more_stores_retailer_skipped", { retailer }); onDone(); }}
          className="h-11 text-[15px] font-extrabold hover:underline"
          style={{ color: ONB_TEXT2 }}
        >
          {t("onboarding2.more_skip_retailer", { retailer: name })}
        </button>
      </div>
    </OnboardingScreen>
  );
}
